import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Alert,
    Box,
    Chip,
    Container,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    CircularProgress,
    IconButton,
    Tooltip,
    alpha,
} from "@mui/material";
import ClassOutlinedIcon from "@mui/icons-material/ClassOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import FolderSharedOutlinedIcon from "@mui/icons-material/FolderSharedOutlined";
import { useFormTeacherHomeroom } from "../../hooks/useFormTeacherHomeroom";
import { fetchClassCurrentTermResources, recordResourceDownload } from "../../utils/termResourceApi";
import { resolveUploadedMediaUrl } from "../../config/api";

const TYPE_OPTIONS = ["All", "PastPaper", "Notes", "Book", "TLM"];
const TYPE_LABELS = {
    PastPaper: "Past paper",
    Notes: "Notes",
    Book: "Book",
    TLM: "TLM",
};

function formatDate(d) {
    if (!d) return "—";
    return new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

export default function FormTeacherClassResources() {
    const { homeroomClassId, homeroomLabel, hasHomeroomClass } = useFormTeacherHomeroom();
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [message, setMessage] = useState("");
    const [typeFilter, setTypeFilter] = useState("All");
    const [subjectFilter, setSubjectFilter] = useState("");

    const load = useCallback(async () => {
        if (!homeroomClassId) {
            setLoading(false);
            return;
        }
        setLoading(true);
        try {
            const result = await fetchClassCurrentTermResources(homeroomClassId, {
                resourceType: typeFilter !== "All" ? typeFilter : undefined,
                subjectId: subjectFilter || undefined,
            });
            setData(result);
            setMessage("");
        } catch (err) {
            setMessage(err.response?.data?.message || "Could not load class resources.");
            setData(null);
        } finally {
            setLoading(false);
        }
    }, [homeroomClassId, typeFilter, subjectFilter]);

    useEffect(() => {
        load();
    }, [load]);

    const subjects = useMemo(() => {
        const list = data?.resources || [];
        const map = new Map();
        for (const r of list) {
            const id = r.subject?.id || r.subject?._id;
            const name = r.subject?.subName || "Other";
            if (id) map.set(id, name);
        }
        return [...map.entries()].map(([id, name]) => ({ id, name }));
    }, [data?.resources]);

    const rows = data?.resources || [];
    const periodLabel = data?.period
        ? [data.period.term, data.period.academicYear].filter(Boolean).join(" · ")
        : "";

    const handleDownload = async (row) => {
        const id = row.id || row._id;
        try {
            await recordResourceDownload(id);
            load();
        } catch {
            /* allow download anyway */
        }
        window.open(resolveUploadedMediaUrl(row.fileUrl), "_blank", "noopener,noreferrer");
    };

    if (!hasHomeroomClass) {
        return (
            <Container maxWidth="md" sx={{ py: 4 }}>
                <Alert severity="info">Assign a homeroom class to view term resources for your learners.</Alert>
            </Container>
        );
    }

    return (
        <Box
            sx={{
                minHeight: "100%",
                py: { xs: 2, sm: 3 },
                background: (t) =>
                    t.palette.mode === "light"
                        ? `linear-gradient(180deg, ${alpha(t.palette.primary.main, 0.04)} 0%, ${t.palette.grey[100]} 20%)`
                        : alpha(t.palette.common.black, 0.12),
            }}
        >
            <Container maxWidth="lg">
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                    <ClassOutlinedIcon color="primary" />
                    <Box>
                        <Typography variant="overline" color="text.secondary" fontWeight={600}>
                            Form teacher
                        </Typography>
                        <Typography variant="h5" fontWeight={800}>
                            Class resources · {homeroomLabel}
                        </Typography>
                        {periodLabel ? (
                            <Chip size="small" label={periodLabel} color="primary" variant="outlined" sx={{ mt: 0.5 }} />
                        ) : null}
                    </Box>
                </Stack>

                {message ? (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setMessage("")}>
                        {message}
                    </Alert>
                ) : null}

                <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: 2, border: (t) => `1px solid ${t.palette.divider}` }}>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                        <FormControl size="small" sx={{ minWidth: 160 }}>
                            <InputLabel>Type</InputLabel>
                            <Select label="Type" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
                                {TYPE_OPTIONS.map((t) => (
                                    <MenuItem key={t} value={t}>
                                        {t === "All" ? "All types" : TYPE_LABELS[t] || t}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ minWidth: 200 }}>
                            <InputLabel>Subject</InputLabel>
                            <Select label="Subject" value={subjectFilter} onChange={(e) => setSubjectFilter(e.target.value)}>
                                <MenuItem value="">All subjects</MenuItem>
                                {subjects.map((s) => (
                                    <MenuItem key={s.id} value={s.id}>
                                        {s.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Chip
                            icon={<FolderSharedOutlinedIcon />}
                            label={`${rows.length} resource${rows.length === 1 ? "" : "s"} this term`}
                            variant="outlined"
                        />
                    </Stack>
                </Paper>

                {loading ? (
                    <Stack alignItems="center" py={6}>
                        <CircularProgress />
                    </Stack>
                ) : rows.length === 0 ? (
                    <Paper sx={{ p: 4, textAlign: "center" }}>
                        <Typography color="text.secondary">
                            No shared resources for this class in the current term yet.
                        </Typography>
                    </Paper>
                ) : (
                    <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2, border: (t) => `1px solid ${t.palette.divider}` }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Title</TableCell>
                                    <TableCell>Subject</TableCell>
                                    <TableCell>Teacher</TableCell>
                                    <TableCell>Type</TableCell>
                                    <TableCell>Downloads</TableCell>
                                    <TableCell>Uploaded</TableCell>
                                    <TableCell align="right">Action</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.map((row) => {
                                    const id = row.id || row._id;
                                    return (
                                        <TableRow key={id} hover>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight={600}>
                                                    {row.title}
                                                </Typography>
                                                {row.description ? (
                                                    <Typography variant="caption" color="text.secondary" display="block">
                                                        {row.description}
                                                    </Typography>
                                                ) : null}
                                            </TableCell>
                                            <TableCell>{row.subject?.subName || "—"}</TableCell>
                                            <TableCell>{row.teacher?.name || "—"}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    size="small"
                                                    label={TYPE_LABELS[row.resourceType] || row.resourceType}
                                                    variant="outlined"
                                                />
                                            </TableCell>
                                            <TableCell>{row.downloadCount ?? 0}</TableCell>
                                            <TableCell>{formatDate(row.createdAt)}</TableCell>
                                            <TableCell align="right">
                                                <Tooltip title="Download">
                                                    <IconButton size="small" onClick={() => handleDownload(row)}>
                                                        <DownloadOutlinedIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Container>
        </Box>
    );
}
